import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { OrderableElementsModel, YellowBrickRoadModel } from '../applicant-service/models/yellow-brick-road-model';

@Injectable({
  providedIn: 'root'
})
export class IterativeApplicationRouterService {
  constructor(
    private readonly router: Router) {
  }

  public back(
    identifiers: Array<string>,
    model: Array<OrderableElementsModel>,
    currentElementId: number,
    jobId: number,
    applicantId: string) {
    let previousElement: OrderableElementsModel = null;

    if (currentElementId === 0) {
      // current element is personal information
      previousElement = model[model.length - 1];
    } else {
      const currentElement = this.getCurrentElement(model, currentElementId);

      const currentIndex = model.indexOf(currentElement);

      if (currentIndex >= 0) {
        previousElement = model[currentIndex - 1];
      }
      if (previousElement === undefined) {
        return;
      }
    }
    
    if (previousElement.type === 'CoverLetter') {
      previousElement = _.find(model, x => x.type === 'Resume');
    }

    if (previousElement !== undefined) {
      this.handleRouting(identifiers, previousElement, jobId, applicantId);
    }
  }

  public canNavigateBackwards(model: Array<OrderableElementsModel>, currentElementId: number) {
    let previousElement: OrderableElementsModel = null;

    if (currentElementId === 0) {
      // current element is personal information
      previousElement = model[model.length - 1];
    } else {
      const currentElement = this.getCurrentElement(model, currentElementId);
      const currentIndex = model.indexOf(currentElement);

      if (currentIndex >= 0) {
        previousElement = model[currentIndex - 1];
      }
    }
    
    return previousElement !== undefined
      && previousElement.type !== 'CoverLetter'
      && previousElement.type !== 'Resume';
  }

  public next(
    identifiers: Array<string>,
    ybr: YellowBrickRoadModel,
    currentElementId: number,
    jobId: number,
    applicantId: string,
    isFirstIterativeApplicationPart: boolean,
    iterativeApplicationPartId: number) {
    const currentElement = this.getCurrentElement(ybr.orderableElements, currentElementId);
    const currentIndex = ybr.orderableElements.indexOf(currentElement);

    let nextElement: OrderableElementsModel = null;
    if (currentIndex >= 0 && currentIndex < ybr.orderableElements.length - 1) {
      nextElement = ybr.orderableElements[currentIndex + 1];
    }

    if (nextElement === undefined || nextElement === null || nextElement.type === 'CoverLetter') {
      if (currentIndex >= 0 && currentIndex < ybr.orderableElements.length - 2) {
        nextElement = ybr.orderableElements[currentIndex + 2];
      } else {
        nextElement = undefined;
      }
      const basePath = [...identifiers, 'jobs', jobId, 'apply', applicantId];
      if ((nextElement === undefined || nextElement === null) && isFirstIterativeApplicationPart) {
        if (ybr.displayProfile) {
          this.router.navigate([...basePath, 'personal-information']);
        } else if (ybr.displayEeoc) {
          this.router.navigate([...basePath, 'voluntary-identification']);
        } else {
          this.router.navigate([...basePath, 'review-and-submit']);
        }
        return;
      } else if ((nextElement === undefined || nextElement === null) && !isFirstIterativeApplicationPart) {
        // navigate to thank you page
        this.router.navigate([...basePath, 'submit', iterativeApplicationPartId]);
        return;
      }
    }

    // navigate to next element
    this.handleRouting(identifiers, nextElement, jobId, applicantId);
  }

  private handleRouting(
    identifiers: Array<string>,
    element: OrderableElementsModel,
    jobId: number,
    applicantId: string) {
    if (element === undefined) {
      return;
    }

    const basePath = [...identifiers, 'jobs', jobId, 'apply', applicantId];
    if (element.type === 'Custom' || element.type === 'AutoScore') {
      this.router.navigate([...basePath, 'survey', element.id]);
    } else if (element.type === 'EducationHistory') {
      this.router.navigate([...basePath, 'education-history']);
    } else if (element.type === 'EmploymentHistory') {
      this.router.navigate([...basePath, 'employment-history']);
    } else if (element.type === 'References') {
      this.router.navigate([...basePath, 'references']);
    }
  }

  private getCurrentElement(model: Array<OrderableElementsModel>, currentElementId: number) {
    return _.find(model, x => x.id === currentElementId);
  }
}
